import type { AutopayOffer } from '@/components/AutopayConsentChoice'

export type PersonFieldName = 'name' | 'email' | 'phone'

export type JoinCustomFieldValue = string | string[] | boolean

export interface IdentityField {
  name: PersonFieldName
  label: string
  required: boolean
}

export interface PublicMembershipLevel {
  id: number
  name: string
  description?: string | null
  fee: string
  price_label?: string
  fee_cents: number
  currency: string
  free: boolean
  discount_code_available?: boolean
  renewal: string
  renewal_period: string
  published_to_join_form: boolean
  kind?: 'individual' | 'organization'
  requires_admin_approval: boolean
  autopay_offer?: AutopayOffer | null
  subtotal: string
  subtotal_cents: number
  tax: string
  tax_cents: number
  total: string
  total_cents: number
  tax_rate?: { id: number; name: string; percentage: string } | null
}

export interface PublicJoinCustomField {
  id: number
  label: string
  applies_to?: 'people' | 'organizations' | 'both'
  field_type:
    | 'text'
    | 'multiline text'
    | 'number'
    | 'date'
    | 'checkbox'
    | 'single-select'
    | 'multi-select'
    | 'URL'
    | 'file'
  choices: string[]
  required: boolean
}

export interface JoinOptionsResponse {
  identity_fields: IdentityField[]
  chapter_setting?: 'hidden' | 'optional' | 'required'
  chapters?: Array<{ id: number; name: string }>
  membership_levels: PublicMembershipLevel[]
  custom_fields: PublicJoinCustomField[]
}

export interface DiscountQuote {
  code: string
  percent: number | null
  fixed_amount: string | null
  fixed_amount_cents: number | null
  gross: string
  gross_cents: number
  reduction: string
  reduction_cents: number
  total: string
  total_cents: number
}

export function discountReductionLabel(quote: DiscountQuote) {
  return quote.percent === null ? `${quote.fixed_amount} fixed` : `${quote.percent}%`
}

export interface PersonForm {
  name: string
  email: string
  phone: string
}

export type OrganizationForm = PersonForm

export interface CreatedJoinApplicationResponse {
  person: {
    id: number
    name: string
    email?: string | null
    phone?: string | null
    contact_type: string
  }
  membership: {
    id: number
    level: string
    status: 'Active' | 'Pending-New'
    member_since_on?: string | null
    renewal_due_on?: string | null
    member_number?: number | null
    person_id: number
  }
  payment?: {
    mode: 'hosted_checkout' | 'offline'
    checkout_url?: string | null
    amount?: string | null
    amount_cents?: number | null
    invoice_number?: number | null
    offline_instructions?: string | null
    unavailable_reason?: string | null
  }
}

export interface AcceptedJoinApplicationResponse {
  received: true
}

export type JoinApplicationResponse =
  | CreatedJoinApplicationResponse
  | AcceptedJoinApplicationResponse

export interface JoinPaymentConfirmationResponse {
  confirmation: { status: 'pending' | 'payment_received' }
  membership: CreatedJoinApplicationResponse['membership']
}

export interface JoinFormErrors {
  membershipLevel?: string
  person: Partial<Record<PersonFieldName, string>>
  organization: Partial<Record<PersonFieldName, string>>
  customFields: Record<string, string>
  form?: string
  chapters?: string
}

export type WizardStep = 'apply' | 'review' | 'confirmation'
